/**
 * Choosing and paying for a plan — design/PROTOTYPE-SPEC.md §16, the Plan &
 * billing card.
 *
 * ## Prices come from `packages/core`, which is a cache of Stripe
 *
 * `PRICING` is a hardcoded copy so this renders without a network round trip
 * (core does no I/O). **Stripe is the source of truth** and `pnpm check:pricing`
 * fails if the two drift — so if a number here ever disagrees with a Stripe
 * invoice, this file is the bug. See packages/core/src/plan/pricing.ts.
 *
 * ## What this component does NOT do
 *
 * It does not change anyone's plan. It asks the API for a Checkout URL and
 * redirects. The plan changes when Stripe's webhook says the money moved, which
 * can be after the customer is already back on this page — so the copy avoids
 * promising an instant switch.
 *
 * The itemised proration quote the spec describes ("Credit — already paid on
 * Starter this month −£15") is deliberately absent: the honest figure is
 * Stripe's upcoming-invoice preview, and CLAUDE.md forbids porting the
 * prototype's placeholder proration maths. Stripe's own Checkout shows the
 * real number on the next screen.
 */
import {
  PLANS,
  PLAN_LABEL,
  PRICING,
  SEAT_LIMIT,
  TRIAL_DAYS,
  yearlySaving,
  type Plan,
} from '@inventory-tracker/core';
import { useState } from 'react';
import { openBillingPortal, startCheckout, type Cycle, type Me } from '../lib/api.js';
import { c, primaryButton, sans, segTab, segTrack } from '../theme.js';
import { cardStyle, money, smallButton } from './format.js';

/** What each tier gives you. Ordered so the differences read top to bottom. */
const FEATURES: Record<Plan, string[]> = {
  starter: [
    'Batch and expiry tracking with FEFO',
    'Stock counts and the full movement ledger',
    'Spreadsheet import and export',
    'Up to 100 products · 2 people',
  ],
  pro: [
    'Everything in Starter',
    'Analytics: where the money went, days of cover, waste and what sells',
    'Promotions, with offers suggested from your own shelves',
    'Up to 400 products · 5 people',
  ],
  premium: [
    'Everything in Pro',
    'Targets: break-even, monthly goal, treatment planning',
    'Unlimited products · unlimited people',
  ],
};

interface Props {
  me: Me;
  /** False for anyone but the owner — the API refuses regardless. */
  mayManage: boolean;
}

export function PlanPicker({ me, mayManage }: Props) {
  const [cycle, setCycle] = useState<Cycle>(me.subscription.cycle);
  const [busy, setBusy] = useState<Plan | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = me.subscription.plan;
  /** A clinic that has never paid has no Stripe customer to manage yet. */
  const hasBilling = me.subscription.status !== 'trialing' || me.subscription.trialEndsAt === null;

  async function go(plan: Plan) {
    setBusy(plan);
    setError(null);
    try {
      const { url } = await startCheckout(plan, cycle);
      // Full navigation, not fetch: Stripe Checkout is a hosted page.
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout');
      setBusy(null);
    }
  }

  async function portal() {
    setBusy('portal');
    setError(null);
    try {
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal');
      setBusy(null);
    }
  }

  return (
    <div data-tour="plancard" style={{ ...cardStyle, marginTop: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: 0 }}>
          Plan &amp; billing
        </h2>
        <div style={segTrack}>
          {(['monthly', 'yearly'] as Cycle[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setCycle(k)}
              style={segTab(cycle === k)}
            >
              {k === 'monthly' ? 'Monthly' : 'Yearly'}
            </button>
          ))}
        </div>
      </div>

      <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 18px', lineHeight: 1.7 }}>
        {cycle === 'yearly'
          ? `Billed once a year. Cancel anytime. You keep the rest of the year.`
          : `Billed monthly. Cancel anytime.`}
      </p>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: 13,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 12,
        }}
      >
        {PLANS.map((plan) => {
          const p = PRICING[plan];
          const isCurrent = plan === current;
          // Once the trial has ended the plan they trialled is not theirs yet:
          // it has to be choosable, or the obvious button is the dead one.
          const alreadyOn =
            !me.subscription.readOnly && isCurrent && cycle === me.subscription.cycle;
          const perMonth = cycle === 'yearly' ? p.yearlyPerMonth : p.monthly;
          const saving = yearlySaving(plan);

          return (
            <div
              key={plan}
              style={{
                border: `1px solid ${isCurrent ? c.green : c.border}`,
                borderRadius: 12,
                padding: 16,
                background: isCurrent ? c.well : 'transparent',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ fontSize: 15 }}>{PLAN_LABEL[plan]}</strong>
                {isCurrent && (
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 600,
                      letterSpacing: '0.6px',
                      color: c.green,
                      border: `1px solid ${c.border}`,
                      borderRadius: 980,
                      padding: '2px 7px',
                    }}
                  >
                    {me.subscription.readOnlyReason === 'trial_ended'
                      ? 'Trialled'
                      : me.subscription.readOnly
                        ? 'Previous'
                        : 'Current'}
                  </span>
                )}
              </div>

              <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 27, fontWeight: 300, margin: '8px 0 2px' }}>
                {money(perMonth)}
                <span style={{ fontSize: 13, color: c.muted, fontFamily: sans }}> /month</span>
              </div>
              <div style={{ fontSize: 12, color: c.muted2, minHeight: 17 }}>
                {cycle === 'yearly' ? `${money(p.yearlyTotal)} a year, save ${money(saving)}` : ''}
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  margin: '14px 0 16px',
                  padding: 0,
                  flex: 1,
                  fontSize: 12.5,
                  color: c.muted,
                  lineHeight: 1.75,
                }}
              >
                {FEATURES[plan].map((f) => (
                  <li key={f} style={{ display: 'flex', gap: 7 }}>
                    <span style={{ color: c.green }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                disabled={!mayManage || busy !== null || alreadyOn}
                onClick={() => void go(plan)}
                style={{
                  ...(isCurrent && !me.subscription.readOnly ? smallButton : primaryButton),
                  width: '100%',
                  height: 40,
                  opacity: !mayManage || alreadyOn ? 0.45 : 1,
                  cursor: mayManage ? 'pointer' : 'not-allowed',
                }}
              >
                {busy === plan
                  ? 'Opening…'
                  : alreadyOn
                    ? 'Current plan'
                    : isCurrent && !me.subscription.readOnly
                      ? `Switch to ${cycle}`
                      : `Choose ${PLAN_LABEL[plan]}`}
              </button>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <p style={{ fontSize: 12.5, color: c.muted2, margin: 0, lineHeight: 1.7, maxWidth: 380 }}>
          {!mayManage
            ? 'Only the owner can change the plan or payment details.'
            : me.subscription.readOnly
              ? "Choosing a plan opens Stripe's secure checkout. Cards, invoices, VAT receipts and cancellation are then in Stripe's billing portal."
              : `Cards, invoices, VAT receipts and cancellation are all in Stripe's billing portal. Your ${TRIAL_DAYS}-day trial needs no card.`}
        </p>
        {mayManage && hasBilling && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void portal()}
            style={{ ...smallButton, height: 38, padding: '0 16px' }}
          >
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
        )}
      </div>

      {/* Seat caps are the limit clinics hit first, so they are stated rather
          than left to be discovered when an invite is refused. */}
      <p style={{ fontSize: 11.5, color: c.faint, margin: '14px 0 0', lineHeight: 1.6 }}>
        Seats: Starter {SEAT_LIMIT.starter} · Pro {SEAT_LIMIT.pro} · Premium unlimited.
        No VAT is added to these prices.
      </p>
    </div>
  );
}
